import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import { productsAPI, reviewsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import toast from 'react-hot-toast';
import {
  HiOutlineHeart,
  HiHeart,
  HiOutlinePlus,
  HiOutlineMinus,
  HiStar,
  HiCheck,
  HiOutlineShoppingBag,
  HiOutlineTruck,
  HiOutlineRefresh,
  HiOutlineShieldCheck,
} from 'react-icons/hi';

const ProductDetail = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { wishlist, addToWishlist, removeFromWishlist, isInWishlist: checkInWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  const isInWishlist = product && checkInWishlist(product._id);

  useEffect(() => {
    fetchProduct();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getBySlug(slug);
      setProduct(response.data.product);
      setRelatedProducts(response.data.relatedProducts || []);

      // Fetch reviews
      const reviewsRes = await reviewsAPI.getByProduct(response.data.product._id);
      setReviews(reviewsRes.data.reviews);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    const variant = product.variants?.[selectedVariant];
    if (!variant || variant.stock === 0) {
      toast.error(t('product.outOfStock'));
      return;
    }

    try {
      await addToCart(product._id, quantity, variant.sku);
      toast.success(t('messages.addedToCart'));
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      toast.error(t('messages.pleaseLogin'));
      return;
    }

    try {
      if (isInWishlist) {
        await removeFromWishlist(product._id);
        toast.success(t('messages.removedFromWishlist'));
      } else {
        await addToWishlist(product._id);
        toast.success(t('messages.addedToWishlist'));
      }
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error(t('messages.pleaseLogin'));
      return;
    }

    setSubmittingReview(true);
    try {
      await reviewsAPI.create({
        product: product._id,
        ...reviewForm,
      });
      toast.success('Review submitted successfully');
      setReviewForm({ rating: 5, title: '', comment: '' });
      fetchProduct();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl mb-4">{t('messages.error')}</h2>
          <Link to="/shop" className="text-luxury-gold hover:underline">
            {t('common.back')}
          </Link>
        </div>
      </div>
    );
  }

  const currentVariant = product.variants?.[selectedVariant];
  const currentPrice = currentVariant?.price || product.price;
  const comparePrice = currentVariant?.compareAtPrice || product.compareAtPrice;
  const hasDiscount = comparePrice && comparePrice > currentPrice;
  const isOutOfStock = currentVariant?.stock === 0;

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-primary-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-luxury-gold">Home</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-luxury-gold">Shop</Link>
            <span>/</span>
            <Link to={`/shop?category=${product.category?.slug}`} className="hover:text-luxury-gold">
              {product.category?.name}
            </Link>
            <span>/</span>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12">
            {/* Image Gallery */}
            <div className="mb-8 lg:mb-0">
              <div className="sticky top-24">
                {/* Main Image */}
                <Swiper
                  modules={[Navigation, Thumbs]}
                  thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                  navigation
                  className="aspect-[3/4] mb-4"
                >
                  {product.images?.map((image, index) => (
                    <SwiperSlide key={index}>
                      <img
                        src={image?.url || image}
                        alt={`${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Thumbnails */}
                {product.images?.length > 1 && (
                  <Swiper
                    modules={[FreeMode, Thumbs]}
                    onSwiper={setThumbsSwiper}
                    spaceBetween={10}
                    slidesPerView={4}
                    freeMode
                    watchSlidesProgress
                    className="thumbs-gallery"
                  >
                    {product.images.map((image, index) => (
                      <SwiperSlide key={index} className="cursor-pointer opacity-50 hover:opacity-100 transition-opacity [&.swiper-slide-thumb-active]:opacity-100">
                        <img
                          src={image?.url || image}
                          alt={`${product.name} thumb - ${index + 1}`}
                          className="aspect-square object-cover"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div>
              {/* Category */}
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                {product.category?.name}
              </p>

              {/* Name */}
              <h1 className="font-serif text-3xl md:text-4xl mb-4">{product.name}</h1>

              {/* Rating */}
              {product.averageRating > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <HiStar
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(product.averageRating)
                            ? 'text-luxury-gold'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">
                    ({product.reviewCount} review{product.reviewCount !== 1 ? 's' : ''})
                  </span>
                </div>
              )}

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl font-medium">${currentPrice.toFixed(2)}</span>
                {hasDiscount && (
                  <>
                    <span className="text-lg text-gray-400 line-through">${comparePrice.toFixed(2)}</span>
                    <span className="bg-luxury-gold text-white text-xs px-2 py-1 uppercase">
                      Save {Math.round((1 - currentPrice / comparePrice) * 100)}%
                    </span>
                  </>
                )}
              </div>

              {/* Short Description */}
              <p className="text-gray-600 leading-relaxed mb-8">
                {product.shortDescription}
              </p>

              {/* Variants */}
              {product.variants && product.variants.length > 1 && (
                <div className="mb-6">
                  <p className="text-sm font-medium mb-3">
                    {t('product.variant')}: <span className="font-normal">{currentVariant?.name}</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((variant, index) => (
                      <button
                        key={variant.sku}
                        onClick={() => setSelectedVariant(index)}
                        disabled={variant.stock === 0}
                        className={`relative px-4 py-2 border text-sm transition-all ${
                          selectedVariant === index
                            ? 'border-luxury-charcoal'
                            : 'border-gray-200 hover:border-gray-400'
                        } ${variant.stock === 0 ? 'opacity-50 cursor-not-allowed line-through' : ''}`}
                      >
                        {variant.colorHex && (
                          <span
                            className="inline-block w-4 h-4 rounded-full mr-2"
                            style={{ backgroundColor: variant.colorHex }}
                          />
                        )}
                        {variant.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock Status */}
              <div className="mb-6">
                {isOutOfStock ? (
                  <span className="text-red-500 text-sm">{t('product.outOfStock')}</span>
                ) : currentVariant?.stock <= 5 ? (
                  <span className="text-orange-500 text-sm">
                    {t('product.lowStock', { count: currentVariant.stock })}
                  </span>
                ) : (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <HiCheck className="w-4 h-4" /> {t('product.inStock')}
                  </span>
                )}
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                {/* Quantity */}
                <div className="flex items-center border border-gray-200">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <HiOutlineMinus className="w-5 h-5" />
                  </button>
                  <span className="w-14 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(currentVariant?.stock || 10, quantity + 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <HiOutlinePlus className="w-5 h-5" />
                  </button>
                </div>

                {/* Add to Cart */}
                <button
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm uppercase tracking-widest transition-colors ${
                    isOutOfStock
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-luxury-charcoal text-white hover:bg-gray-800'
                  }`}
                >
                  <HiOutlineShoppingBag className="w-5 h-5" />
                  {isOutOfStock ? t('product.outOfStock') : t('product.addToCart')}
                </button>

                {/* Wishlist */}
                <button
                  onClick={handleWishlistToggle}
                  className="p-4 border border-gray-200 hover:border-luxury-gold transition-colors"
                >
                  {isInWishlist ? (
                    <HiHeart className="w-5 h-5 text-luxury-gold" />
                  ) : (
                    <HiOutlineHeart className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Features */}
              <div className="border-t border-gray-100 pt-8 space-y-4">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <HiOutlineTruck className="w-5 h-5" />
                  <span>{t('product.freeShippingOver')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <HiOutlineRefresh className="w-5 h-5" />
                  <span>{t('product.easyReturns')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <HiOutlineShieldCheck className="w-5 h-5" />
                  <span>{t('product.secureCheckout')}</span>
                </div>
              </div>

              {/* SKU & Categories */}
              <div className="mt-8 pt-8 border-t border-gray-100 text-sm text-gray-500">
                <p>{t('product.sku')}: {currentVariant?.sku}</p>
                <p>{t('product.category')}: {product.category?.name}</p>
                {product.tags?.length > 0 && (
                  <p>{t('product.tags')}: {product.tags.join(', ')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Headers */}
          <div className="flex justify-center gap-8 border-b border-gray-100 mb-8">
            {['description', 'details', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-sm uppercase tracking-widest transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-luxury-charcoal text-luxury-charcoal'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {tab === 'description' ? t('product.description') : tab === 'details' ? t('product.details') : `${t('product.reviews')} (${reviews.length})`}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="max-w-3xl mx-auto">
            {activeTab === 'description' && (
              <div
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            {activeTab === 'details' && (
              <div className="space-y-4">
                {product.specifications?.map((spec, index) => (
                  <div key={index} className="flex border-b border-gray-100 pb-4">
                    <span className="w-1/3 text-gray-500">{spec.name}</span>
                    <span className="w-2/3">{spec.value}</span>
                  </div>
                ))}
                {(!product.specifications || product.specifications.length === 0) && (
                  <p className="text-gray-500 text-center">No specifications available</p>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                {/* Reviews List */}
                {reviews.length > 0 ? (
                  <div className="space-y-6 mb-12">
                    {reviews.map((review) => (
                      <div key={review._id} className="border-b border-gray-100 pb-6">
                        <div className="flex items-center gap-4 mb-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium">
                              {review.user?.firstName?.[0]}{review.user?.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{review.user?.firstName} {review.user?.lastName}</p>
                            <div className="flex items-center gap-2">
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <HiStar
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= review.rating ? 'text-luxury-gold' : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-xs text-gray-400">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        {review.title && (
                          <h4 className="font-medium mb-2">{review.title}</h4>
                        )}
                        <p className="text-gray-600">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 mb-12">
                    {t('product.noReviews')}
                  </p>
                )}

                {/* Review Form */}
                {user ? (
                  <div className="bg-primary-50 p-6">
                    <h3 className="font-serif text-xl mb-4">{t('product.writeReview')}</h3>
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                      <div>
                        <label className="block text-sm mb-2">Rating</label>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            >
                              <HiStar
                                className={`w-8 h-8 ${
                                  star <= reviewForm.rating ? 'text-luxury-gold' : 'text-gray-300'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2">Title (optional)</label>
                        <input
                          type="text"
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                          placeholder="Summarize your review"
                        />
                      </div>
                      <div>
                        <label className="block text-sm mb-2">Review</label>
                        <textarea
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          required
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold resize-none"
                          placeholder="Share your experience with this product"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submittingReview}
                        className="bg-luxury-charcoal text-white px-8 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:opacity-50"
                      >
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="text-center bg-primary-50 p-6">
                    <p className="text-gray-600 mb-4">{t('messages.pleaseLogin')}</p>
                    <Link
                      to="/login"
                      className="inline-block bg-luxury-charcoal text-white px-8 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
                    >
                      Login
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-2xl md:text-3xl text-center mb-10">{t('product.youMayAlsoLike')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
